/**
 * The curate screen's view state, in one place.
 *
 * It was six `useState` calls inside the screen. It is the same six, lifted out
 * so that something other than a click can move them -- specifically the
 * copilot, through `applyIntents`. The screen keeps its setters and reads
 * exactly as it did; nothing about how it works has changed.
 */

import { useCallback, useState } from "react";
import { INITIAL_STATE, applyIntents } from "../assistant/intents";

export function useCurationState() {
  const [state, setState] = useState(INITIAL_STATE);

  const update = useCallback((patch) => {
    setState((was) => ({ ...was, ...(typeof patch === "function" ? patch(was) : patch) }));
  }, []);

  const apply = useCallback((intents) => {
    setState((was) => applyIntents(was, intents));
  }, []);

  /** Opening a different curve clears what belonged to the last one. */
  const openView = useCallback((key) => {
    setState((was) => ({
      ...was,
      selectedView: key,
      activeCondition: null,
      selected: [],
      highlighted: null,
    }));
  }, []);

  const clearPendingScreen = useCallback(() => {
    setState((was) => (was.pendingScreen ? { ...was, pendingScreen: null } : was));
  }, []);

  // Stable identities: the screen passes several of these into `useCallback`
  // and into a memoised child, and a setter rebuilt on every render would
  // quietly defeat both.
  const setSelectedView = useCallback((key) => update({ selectedView: key }), [update]);
  const setActiveCondition = useCallback(
    (condition) => update({ activeCondition: condition }),
    [update]
  );
  const setSelected = useCallback(
    (next) =>
      update((was) => ({ selected: typeof next === "function" ? next(was.selected) : next })),
    [update]
  );
  const setHighlighted = useCallback((id) => update({ highlighted: id }), [update]);
  const setShowOutliers = useCallback((show) => update({ showOutliers: show }), [update]);
  const setHiddenConditions = useCallback(
    (next) =>
      update((was) => ({
        hiddenConditions: typeof next === "function" ? next(was.hiddenConditions) : next,
      })),
    [update]
  );

  return {
    ...state,
    setSelectedView,
    setActiveCondition,
    setSelected,
    setHighlighted,
    setShowOutliers,
    setHiddenConditions,
    openView,
    apply,
    clearPendingScreen,
  };
}
